const servicesData = [
  {
    id: 1,
    slug: "software-development",
    title: "Software Development",
    shortDesc: "Tailored software solutions engineered to streamline your business operations and accelerate growth.",
    icon: "ri-code-s-slash-line",
    color: "blue",
    image: "https://images.unsplash.com/photo-1555066931-4365d14bab8c?w=800&h=400&fit=crop",
    approach: [
      {
        title: "Requirement Analysis",
        desc: "We begin with in-depth consultations to map out your business workflows, identify bottlenecks, and define clear functional specifications that align with your organizational goals."
      },
      {
        title: "Architecture & Design",
        desc: "Our architects design scalable, secure system blueprints using industry-standard patterns such as microservices, event-driven architecture, and modular monoliths based on your project scope."
      },
      {
        title: "Agile Development",
        desc: "Development follows iterative sprints with continuous client feedback. We use modern frameworks and coding standards to ensure clean, maintainable, and well-documented code."
      },
      {
        title: "Quality Assurance",
        desc: "Every release undergoes rigorous testing cycles including unit testing, integration testing, performance benchmarking, and security vulnerability assessments before deployment."
      },
      {
        title: "Deployment & Support",
        desc: "We handle seamless deployment to your preferred environment and provide ongoing maintenance, bug fixes, feature enhancements, and performance monitoring post-launch."
      }
    ],
    whyChooseUs: [
      {
        title: "Domain Expertise",
        desc: "Our developers have delivered successful projects across healthcare, finance, education, retail, and logistics — giving us deep insight into industry-specific challenges and compliance requirements."
      },
      {
        title: "Technology Agnostic",
        desc: "We recommend the best technology stack for your unique needs rather than forcing a one-size-fits-all approach. From .NET and Java to Node.js and Python, we pick what works best."
      },
      {
        title: "Transparent Process",
        desc: "You receive regular progress updates, access to staging environments, and direct communication channels with your development team throughout the entire project lifecycle."
      },
      {
        title: "Long-Term Partnership",
        desc: "We build software that grows with your business. Our solutions are designed for scalability, and we offer flexible AMC plans for continued support and feature development."
      }
    ],
    tools: ["Java", "Python", ".NET", "Node.js", "PostgreSQL", "MySQL", "Docker", "AWS"],
    faq: [
      {
        q: "How long does custom software development typically take?",
        a: "Project timelines vary based on complexity. A basic business application may take 6-8 weeks, while enterprise-grade systems can range from 3-6 months. We provide a detailed timeline after the initial requirement analysis phase."
      },
      {
        q: "Do you provide source code ownership?",
        a: "Yes, upon project completion and final payment, complete source code ownership is transferred to you. We believe in full transparency and client empowerment."
      },
      {
        q: "Can you integrate with our existing systems?",
        a: "Absolutely. We specialize in building integrations with existing ERP, CRM, accounting software, and third-party APIs to ensure seamless data flow across your technology ecosystem."
      }
    ]
  },
  {
    id: 2,
    slug: "web-development",
    title: "Web Development",
    shortDesc: "Responsive, high-performance websites and web applications that captivate visitors and convert them into loyal customers.",
    icon: "ri-global-line",
    color: "orange",
    image: "https://images.unsplash.com/photo-1547658719-da2b51169166?w=800&h=400&fit=crop",
    approach: [
      {
        title: "Discovery & Strategy",
        desc: "We study your target audience, competitor landscape, and business objectives to craft a web strategy that positions your brand effectively in the digital marketplace."
      },
      {
        title: "Wireframing & Prototyping",
        desc: "Interactive wireframes and clickable prototypes are created to visualize the user journey, information architecture, and page layouts before a single line of code is written."
      },
      {
        title: "Frontend & Backend Development",
        desc: "Our frontend developers build pixel-perfect, responsive interfaces while our backend engineers create robust APIs, databases, and server-side logic to power your application."
      },
      {
        title: "Content & SEO Integration",
        desc: "We integrate SEO best practices from the ground up — semantic HTML, optimized meta tags, fast load times, structured data, and mobile-first design principles."
      },
      {
        title: "Launch, Monitor & Optimize",
        desc: "Post-launch, we monitor performance metrics, user behavior analytics, and conversion funnels to continuously refine and improve your website's effectiveness."
      }
    ],
    whyChooseUs: [
      {
        title: "Mobile-First Approach",
        desc: "Over 70 percent of web traffic comes from mobile devices. Every website we build is designed mobile-first and then enhanced for larger screens, ensuring flawless experiences everywhere."
      },
      {
        title: "Performance Optimized",
        desc: "We achieve Google PageSpeed scores of 90+ through code splitting, image optimization, lazy loading, CDN integration, and efficient caching strategies."
      },
      {
        title: "CMS Empowerment",
        desc: "We build on platforms like WordPress, Shopify, and custom CMS solutions that empower your team to manage content, products, and pages without developer assistance."
      },
      {
        title: "Conversion Focused",
        desc: "Beautiful design means nothing without results. We incorporate proven UX patterns, clear CTAs, trust signals, and A/B testing to maximize your conversion rates."
      }
    ],
    tools: ["React", "Next.js", "Vue.js", "Angular", "WordPress", "Shopify", "Laravel", "Tailwind CSS"],
    faq: [
      {
        q: "Should I choose a custom website or a CMS-based solution?",
        a: "It depends on your needs. If you require frequent content updates and have a non-technical team, a CMS like WordPress is ideal. For highly specialized functionality or unique user experiences, a custom-built application offers more flexibility."
      },
      {
        q: "Will my website be SEO-friendly?",
        a: "Yes. Every website we build follows technical SEO best practices including fast loading speed, mobile responsiveness, semantic markup, proper heading hierarchy, XML sitemaps, and clean URL structures."
      },
      {
        q: "Do you provide hosting and domain services?",
        a: "Yes, we offer end-to-end solutions including domain registration, hosting setup on AWS, Azure, or shared hosting platforms, SSL certificate installation, and email configuration."
      }
    ]
  },
  {
    id: 3,
    slug: "mobile-app-development",
    title: "Mobile App Development",
    shortDesc: "Native and cross-platform mobile applications designed to deliver seamless user experiences on every device.",
    icon: "ri-smartphone-line",
    color: "green",
    image: "https://images.unsplash.com/photo-1512941937669-90a1b58e7e9c?w=800&h=400&fit=crop",
    approach: [
      {
        title: "Concept Validation",
        desc: "We validate your app idea through market research, competitor analysis, and user persona development to ensure there is genuine demand and a clear path to monetization."
      },
      {
        title: "UX Research & Design",
        desc: "Our designers create intuitive user flows, wireframes, and high-fidelity mockups that prioritize ease of use, accessibility, and visual appeal across iOS and Android platforms."
      },
      {
        title: "Cross-Platform Development",
        desc: "Using frameworks like Flutter and React Native, we build applications that run natively on both iOS and Android from a single codebase — reducing cost and time to market."
      },
      {
        title: "API & Backend Integration",
        desc: "We develop secure RESTful APIs and integrate with payment gateways, push notification services, analytics platforms, and any third-party systems your app requires."
      },
      {
        title: "App Store Submission & Growth",
        desc: "We handle the entire submission process for Google Play Store and Apple App Store, including ASO optimization, and provide post-launch analytics and iteration support."
      }
    ],
    whyChooseUs: [
      {
        title: "Faster Time to Market",
        desc: "Our cross-platform development approach using Flutter and React Native delivers apps to both platforms simultaneously, cutting development time by up to 40 percent compared to native-only builds."
      },
      {
        title: "Offline Capability",
        desc: "We implement intelligent offline-first architectures with local data caching and sync mechanisms, ensuring your app remains functional even without an active internet connection."
      },
      {
        title: "Scalable Architecture",
        desc: "Our apps are built on scalable architectures that handle growing user bases, increasing data volumes, and expanding feature sets without performance degradation."
      },
      {
        title: "End-to-End Security",
        desc: "From encrypted data storage and secure API communication to biometric authentication and GDPR compliance, we implement comprehensive security measures at every layer."
      }
    ],
    tools: ["Flutter", "React Native", "Swift", "Kotlin", "Firebase", "Node.js", "MongoDB", "AWS Amplify"],
    faq: [
      {
        q: "Should I build a native app or a cross-platform app?",
        a: "For most business applications, cross-platform frameworks like Flutter or React Native offer the best balance of performance, cost, and development speed. Native development is recommended only for apps requiring intensive graphics, complex animations, or deep platform-specific integrations."
      },
      {
        q: "How much does mobile app development cost?",
        a: "Costs vary based on features, complexity, and platforms. A basic app starts around 1.5-3 lakhs, while complex applications with custom integrations can range from 5-15 lakhs or more. We provide detailed estimates after the discovery phase."
      },
      {
        q: "Do you provide app maintenance after launch?",
        a: "Yes, we offer monthly and annual maintenance plans that include bug fixes, OS compatibility updates, performance optimization, feature additions, and app store compliance updates."
      }
    ]
  },
  {
    id: 4,
    slug: "ui-ux-design",
    title: "UI/UX Design",
    shortDesc: "Human-centered design solutions that transform complex workflows into intuitive, delightful digital experiences.",
    icon: "ri-palette-line",
    color: "red",
    image: "https://images.unsplash.com/photo-1561070791-2526d30994b5?w=800&h=400&fit=crop",
    approach: [
      {
        title: "User Research",
        desc: "We conduct interviews, surveys, and usability studies to understand your users' motivations, pain points, and behavioral patterns — forming the foundation of every design decision."
      },
      {
        title: "Information Architecture",
        desc: "We organize content and features into logical structures using card sorting, sitemap creation, and navigation flow mapping to ensure users can find what they need effortlessly."
      },
      {
        title: "Wireframing",
        desc: "Low-fidelity wireframes establish the layout, content hierarchy, and interaction patterns for each screen, allowing rapid iteration before investing in visual design."
      },
      {
        title: "Visual Design & Prototyping",
        desc: "High-fidelity designs bring your brand to life with carefully chosen typography, color palettes, iconography, and micro-interactions. Interactive prototypes enable real user testing."
      },
      {
        title: "Usability Testing & Handoff",
        desc: "We validate designs with real users, gather feedback, iterate on problem areas, and deliver developer-ready specifications with style guides and component libraries."
      }
    ],
    whyChooseUs: [
      {
        title: "Research-Driven Decisions",
        desc: "Every design choice is backed by user research data, not personal preferences. This evidence-based approach dramatically reduces the risk of building features that users do not want or cannot use."
      },
      {
        title: "Accessibility First",
        desc: "We design for everyone. Our interfaces meet WCAG 2.1 accessibility standards, ensuring usability for people with diverse abilities including visual, motor, cognitive, and auditory differences."
      },
      {
        title: "Design Systems",
        desc: "We create comprehensive design systems with reusable components, tokens, and guidelines that ensure visual consistency across your entire product ecosystem and speed up future development."
      },
      {
        title: "Measurable Impact",
        desc: "We define success metrics upfront — task completion rates, time-on-task, error rates, and satisfaction scores — and measure improvement after design implementation to prove ROI."
      }
    ],
    tools: ["Figma", "Adobe XD", "Sketch", "InVision", "Miro", "Hotjar", "Maze", "Zeplin"],
    faq: [
      {
        q: "What is the difference between UI and UX design?",
        a: "UX (User Experience) design focuses on the overall feel of the product — how easy it is to use, how logical the navigation is, and how well it solves user problems. UI (User Interface) design focuses on the visual layer — colors, typography, buttons, and visual hierarchy. Both work together to create a successful product."
      },
      {
        q: "Can you redesign our existing application?",
        a: "Absolutely. We start with a UX audit of your current product, identifying usability issues, conversion barriers, and design inconsistencies. We then create a phased redesign plan that minimizes disruption while maximizing improvement."
      },
      {
        q: "How long does the design process take?",
        a: "A typical design project takes 4-8 weeks depending on the number of screens, complexity of interactions, and the extent of user research required. We provide a detailed timeline during the project kickoff."
      }
    ]
  },
  {
    id: 5,
    slug: "seo",
    title: "Search Engine Optimization",
    shortDesc: "Data-driven SEO strategies that elevate your search rankings, drive organic traffic, and generate qualified leads.",
    icon: "ri-search-line",
    color: "purple",
    image: "https://images.unsplash.com/photo-1562577309-4932fdd64cd1?w=800&h=400&fit=crop",
    approach: [
      {
        title: "Technical SEO Audit",
        desc: "We perform a comprehensive audit of your website's technical health — crawlability, indexation, site speed, mobile usability, schema markup, and Core Web Vitals — to identify and fix issues that hinder rankings."
      },
      {
        title: "Keyword Research & Strategy",
        desc: "Using advanced tools and competitor analysis, we identify high-value keywords with strong search volume and achievable difficulty scores, mapped to specific pages and content clusters."
      },
      {
        title: "On-Page Optimization",
        desc: "We optimize title tags, meta descriptions, heading structures, internal linking, image alt texts, and content quality across all target pages to maximize relevance signals for search engines."
      },
      {
        title: "Content & Link Building",
        desc: "We develop authoritative, long-form content that earns natural backlinks and build strategic relationships for high-quality link acquisition from relevant industry publications."
      },
      {
        title: "Reporting & Iteration",
        desc: "Monthly performance reports track keyword rankings, organic traffic, click-through rates, and conversion metrics. We continuously refine our strategy based on data insights and algorithm updates."
      }
    ],
    whyChooseUs: [
      {
        title: "White Hat Techniques Only",
        desc: "We strictly follow Google's Webmaster Guidelines and employ only ethical, sustainable SEO practices. No shortcuts, no black hat tactics — just solid strategies that deliver lasting results."
      },
      {
        title: "Local SEO Specialization",
        desc: "For businesses serving specific geographic areas, we optimize Google Business Profile, local citations, location-specific landing pages, and review management to dominate local search results."
      },
      {
        title: "Transparent Reporting",
        desc: "You receive detailed monthly reports with clear metrics — keyword position changes, traffic trends, backlink profiles, and revenue attribution — so you always know exactly what you are getting."
      },
      {
        title: "Industry Experience",
        desc: "We have ranked websites across competitive industries including healthcare, real estate, e-commerce, education, and technology — giving us proven playbooks for diverse markets."
      }
    ],
    tools: ["Google Analytics", "Google Search Console", "SEMrush", "Ahrefs", "Screaming Frog", "Moz", "Surfer SEO", "GTmetrix"],
    faq: [
      {
        q: "How long does it take to see SEO results?",
        a: "SEO is a long-term investment. Most websites begin seeing meaningful improvements in organic traffic and rankings within 3-6 months. Competitive industries or new domains may take 6-12 months for significant results."
      },
      {
        q: "Do you guarantee first-page rankings?",
        a: "No ethical SEO agency can guarantee specific rankings because search algorithms consider hundreds of ranking factors, many of which are outside our control. What we do guarantee is proven strategies, transparent communication, and measurable improvements."
      },
      {
        q: "What is the difference between on-page and off-page SEO?",
        a: "On-page SEO involves optimizing elements on your own website — content, HTML tags, site structure, and internal links. Off-page SEO involves activities outside your website that impact rankings — primarily backlinks from other reputable websites, social signals, and brand mentions."
      }
    ]
  },
  {
    id: 6,
    slug: "social-media-marketing",
    title: "Social Media Marketing",
    shortDesc: "Strategic social media campaigns that build brand awareness, foster community engagement, and drive measurable business outcomes.",
    icon: "ri-share-forward-line",
    color: "teal",
    image: "https://images.unsplash.com/photo-1611162617474-5b21e879e113?w=800&h=400&fit=crop",
    approach: [
      {
        title: "Platform Analysis",
        desc: "We identify which social platforms your target audience frequents most and develop platform-specific strategies for maximum reach and engagement rather than spreading resources thin across every network."
      },
      {
        title: "Content Strategy & Calendar",
        desc: "We create a comprehensive content calendar with a balanced mix of educational, entertaining, and promotional content tailored to each platform's unique format and audience expectations."
      },
      {
        title: "Content Creation & Publishing",
        desc: "Our creative team produces high-quality graphics, videos, reels, stories, carousels, and copy that align with your brand voice and resonate with your audience's interests and aspirations."
      },
      {
        title: "Community Management",
        desc: "We actively engage with your audience through timely responses to comments, messages, and mentions — building genuine relationships that transform followers into brand advocates."
      },
      {
        title: "Analytics & Optimization",
        desc: "We track engagement rates, reach, follower growth, click-through rates, and conversion metrics to continuously optimize content strategy and maximize return on your social media investment."
      }
    ],
    whyChooseUs: [
      {
        title: "Platform-Native Content",
        desc: "We do not recycle the same content across all platforms. Each piece is crafted specifically for the platform it appears on — Instagram Reels, LinkedIn articles, Twitter threads, or YouTube Shorts — maximizing native engagement."
      },
      {
        title: "Trend Awareness",
        desc: "Our team stays ahead of social media trends, algorithm changes, and emerging features. We leverage trending formats, sounds, and conversations to keep your brand relevant and visible."
      },
      {
        title: "ROI Focused",
        desc: "Vanity metrics look good on reports but do not pay the bills. We focus on metrics that matter — website traffic, lead generation, conversion rates, and customer acquisition cost from social channels."
      },
      {
        title: "Authentic Brand Voice",
        desc: "We develop and maintain a consistent, authentic brand voice across all platforms that reflects your values, resonates with your audience, and differentiates you from competitors."
      }
    ],
    tools: ["Meta Business Suite", "LinkedIn Campaign Manager", "Canva", "Hootsuite", "Buffer", "Sprout Social", "Later", "CapCut"],
    faq: [
      {
        q: "Which social media platforms should my business be on?",
        a: "This depends on your target audience and business type. B2B companies typically benefit most from LinkedIn and Twitter. B2C brands often see strong results on Instagram, Facebook, and YouTube. We analyze your audience demographics and recommend the platforms with the highest potential ROI."
      },
      {
        q: "How often should we post on social media?",
        a: "Posting frequency varies by platform. Instagram and Facebook perform well with 3-5 posts per week, LinkedIn with 2-3 posts per week, and Twitter with daily engagement. Quality always matters more than quantity — one excellent post outperforms five mediocre ones."
      },
      {
        q: "Can social media marketing directly generate sales?",
        a: "Yes. Through strategic content funnels, targeted advertising, social commerce features, and direct messaging workflows, social media can be a significant revenue channel. We build complete social-to-sale pathways tailored to your business model."
      }
    ]
  },
  {
    id: 7,
    slug: "google-ads-ppc",
    title: "Google Ads & PPC Management",
    shortDesc: "Precision-targeted pay-per-click campaigns that maximize your advertising budget and deliver instant, qualified traffic.",
    icon: "ri-advertisement-line",
    color: "blue",
    image: "https://images.unsplash.com/photo-1607703703520-bb638e85caf2?w=800&h=400&fit=crop",
    approach: [
      {
        title: "Campaign Strategy",
        desc: "We define clear campaign objectives — brand awareness, lead generation, or direct sales — and select the most appropriate Google Ads campaign types including Search, Display, Shopping, Video, and Performance Max."
      },
      {
        title: "Keyword & Audience Research",
        desc: "We identify high-intent keywords, build tightly themed ad groups, define negative keyword lists, and create audience segments based on demographics, interests, and behavioral signals."
      },
      {
        title: "Ad Creation & Landing Pages",
        desc: "Our copywriters craft compelling ad copy with strong CTAs, and our designers build dedicated landing pages optimized for conversion — ensuring the promise made in the ad is fulfilled on the page."
      },
      {
        title: "Bid Management & Optimization",
        desc: "We implement smart bidding strategies, conduct A/B tests on ad variations, adjust bids based on device, location, and time performance, and continuously optimize for lower cost-per-acquisition."
      },
      {
        title: "Reporting & Scaling",
        desc: "Weekly and monthly reports detail impressions, clicks, conversions, cost-per-click, and return on ad spend. Once we identify winning campaigns, we strategically scale budget allocation for maximum growth."
      }
    ],
    whyChooseUs: [
      {
        title: "Certified Google Partners",
        desc: "Our team holds Google Ads certifications across Search, Display, Shopping, and Video campaigns. This expertise ensures your campaigns follow Google's latest best practices and leverage advanced features."
      },
      {
        title: "Conversion Tracking Setup",
        desc: "We implement comprehensive conversion tracking including form submissions, phone calls, e-commerce transactions, and micro-conversions — giving you complete visibility into campaign profitability."
      },
      {
        title: "Landing Page Optimization",
        desc: "A great ad with a poor landing page wastes money. We design and optimize dedicated landing pages with clear messaging, fast load times, trust signals, and frictionless conversion paths."
      },
      {
        title: "Budget Efficiency",
        desc: "Every rupee of your advertising budget is treated as an investment. We aggressively eliminate wasted spend through negative keywords, bid adjustments, and continuous performance monitoring."
      }
    ],
    tools: ["Google Ads", "Google Analytics 4", "Google Tag Manager", "Google Looker Studio", "SEMrush", "Unbounce", "Hotjar", "SpyFu"],
    faq: [
      {
        q: "What is the minimum budget for Google Ads?",
        a: "There is no fixed minimum, but we recommend starting with at least 15,000-25,000 rupees per month for Search campaigns to generate sufficient data for optimization. Higher budgets allow for faster learning and scaling."
      },
      {
        q: "How quickly can I see results from PPC campaigns?",
        a: "Unlike SEO, PPC campaigns can drive traffic within hours of launch. However, optimal performance typically develops over 2-4 weeks as we gather data, test ad variations, and refine targeting parameters."
      },
      {
        q: "What is a good return on ad spend (ROAS)?",
        a: "A healthy ROAS varies by industry and profit margins. Generally, a 3:1 to 5:1 return (earning 3-5 rupees for every 1 rupee spent) is considered strong. We work with you to establish benchmarks based on your specific business economics."
      }
    ]
  },
  {
    id: 8,
    slug: "email-marketing",
    title: "Email Marketing",
    shortDesc: "Personalized email campaigns and automated sequences that nurture leads, retain customers, and drive consistent revenue.",
    icon: "ri-mail-send-line",
    color: "orange",
    image: "https://images.unsplash.com/photo-1596526131083-e8c633c948d2?w=800&h=400&fit=crop",
    approach: [
      {
        title: "List Building & Segmentation",
        desc: "We help you build a quality email subscriber base through lead magnets, opt-in forms, and gated content. Subscribers are segmented by behavior, demographics, purchase history, and engagement level."
      },
      {
        title: "Campaign Strategy & Design",
        desc: "We design visually compelling, mobile-responsive email templates and craft a content strategy that includes welcome sequences, nurture campaigns, promotional offers, and re-engagement flows."
      },
      {
        title: "Marketing Automation",
        desc: "We set up automated email workflows triggered by specific user actions — abandoned carts, website visits, content downloads, or purchase milestones — delivering the right message at the right moment."
      },
      {
        title: "A/B Testing",
        desc: "We systematically test subject lines, send times, content formats, CTAs, and design elements to identify what resonates best with each audience segment and continuously improve performance."
      },
      {
        title: "Deliverability & Compliance",
        desc: "We maintain sender reputation through proper authentication (SPF, DKIM, DMARC), list hygiene, and compliance with regulations like GDPR and CAN-SPAM to ensure your emails reach the inbox."
      }
    ],
    whyChooseUs: [
      {
        title: "Highest ROI Channel",
        desc: "Email marketing consistently delivers the highest return on investment of any digital marketing channel — averaging 36 to 42 rupees returned for every 1 rupee spent when executed strategically."
      },
      {
        title: "Personalization at Scale",
        desc: "We leverage dynamic content, behavioral triggers, and predictive analytics to deliver personalized experiences to thousands of subscribers simultaneously — making each recipient feel individually valued."
      },
      {
        title: "Full Funnel Coverage",
        desc: "From awareness-stage educational content to bottom-funnel conversion offers and post-purchase loyalty campaigns, we build email strategies that support every stage of the customer journey."
      },
      {
        title: "Data-Driven Optimization",
        desc: "We track open rates, click-through rates, conversion rates, revenue per email, and unsubscribe rates to continuously optimize every element of your email marketing program."
      }
    ],
    tools: ["Mailchimp", "SendGrid", "HubSpot", "ConvertKit", "ActiveCampaign", "Brevo", "Klaviyo", "Litmus"],
    faq: [
      {
        q: "How often should we send marketing emails?",
        a: "Frequency depends on your audience and content quality. Most businesses perform well with 1-2 emails per week. The key is maintaining value in every send — if you do not have something valuable to share, it is better to skip a send than damage engagement."
      },
      {
        q: "How do we grow our email list organically?",
        a: "Effective strategies include offering valuable lead magnets (e-books, templates, webinars), adding opt-in forms to high-traffic pages, using exit-intent popups, running social media contests, and creating gated premium content."
      },
      {
        q: "What is email marketing automation?",
        a: "Automation uses predefined rules and triggers to send targeted emails automatically. For example, when someone downloads a whitepaper, they automatically receive a series of follow-up emails over the next two weeks — all without manual intervention."
      }
    ]
  },
  {
    id: 9,
    slug: "graphic-design",
    title: "Graphic Design",
    shortDesc: "Visually stunning designs that communicate your brand story, capture attention, and leave a lasting impression on your audience.",
    icon: "ri-brush-line",
    color: "pink",
    image: "https://images.unsplash.com/photo-1626785774573-4b799315345d?w=800&h=400&fit=crop",
    approach: [
      {
        title: "Brand Discovery",
        desc: "We immerse ourselves in your brand story, values, target audience, and competitive landscape to develop a deep understanding of the visual language that will best represent your identity."
      },
      {
        title: "Concept Development",
        desc: "Our designers create multiple concept directions exploring different visual approaches — color palettes, typography pairings, layout styles, and graphic elements — for your review and feedback."
      },
      {
        title: "Design Refinement",
        desc: "Based on your feedback, we refine the chosen direction through multiple revision rounds, perfecting every detail from spacing and alignment to color gradients and shadow effects."
      },
      {
        title: "Asset Delivery",
        desc: "Final designs are delivered in all required formats — print-ready (CMYK, 300 DPI, PDF/A), web-optimized (RGB, PNG, SVG, WebP), and social media dimensions — organized in a clear folder structure."
      },
      {
        title: "Brand Guidelines",
        desc: "We document all design decisions in comprehensive brand guidelines covering logo usage, color codes, typography rules, imagery style, and application examples to ensure consistency across all touchpoints."
      }
    ],
    whyChooseUs: [
      {
        title: "Strategic Creativity",
        desc: "Our designs are not just aesthetically pleasing — they are strategically crafted to achieve specific business objectives, whether that is building brand recognition, conveying professionalism, or driving action."
      },
      {
        title: "Versatile Expertise",
        desc: "From print collateral and digital assets to packaging, signage, and social media templates, our team handles the full spectrum of graphic design needs under one roof."
      },
      {
        title: "Quick Turnaround",
        desc: "We understand that business moves fast. Our streamlined design process and experienced team enable us to deliver high-quality designs within tight deadlines without compromising quality."
      },
      {
        title: "Unlimited Revisions",
        desc: "We work until you are completely satisfied. Our revision process is collaborative and iterative, ensuring the final output perfectly matches your vision and exceeds your expectations."
      }
    ],
    tools: ["Adobe Photoshop", "Adobe Illustrator", "Adobe InDesign", "Figma", "Canva Pro", "CorelDRAW", "After Effects", "Blender"],
    faq: [
      {
        q: "What graphic design services do you offer?",
        a: "We offer a complete range including logo design, business cards, letterheads, brochures, flyers, posters, banners, social media templates, email templates, presentation decks, packaging design, and brand identity systems."
      },
      {
        q: "How many logo concepts will I receive?",
        a: "Our standard logo design package includes 3-5 unique concept directions. From there, we refine your chosen concept through multiple revision rounds until you are completely satisfied with the final result."
      },
      {
        q: "Do I get the source files?",
        a: "Yes. Upon project completion, you receive all source files (AI, PSD, INDD), exported formats (PNG, JPG, SVG, PDF), and a brand guidelines document — giving you full ownership and flexibility for future use."
      }
    ]
  },
  {
    id: 10,
    slug: "cloud-services",
    title: "Cloud Services",
    shortDesc: "Cloud infrastructure design, migration, and management services that ensure scalability, security, and cost efficiency.",
    icon: "ri-cloud-line",
    color: "blue",
    image: "https://images.unsplash.com/photo-1544197150-b99a580bb7a8?w=800&h=400&fit=crop",
    approach: [
      {
        title: "Infrastructure Assessment",
        desc: "We evaluate your current IT infrastructure, workloads, and business requirements to determine the optimal cloud strategy — whether public, private, hybrid, or multi-cloud deployment."
      },
      {
        title: "Cloud Architecture Design",
        desc: "Our cloud architects design fault-tolerant, highly available architectures with auto-scaling, load balancing, and disaster recovery capabilities tailored to your performance and budget requirements."
      },
      {
        title: "Migration Planning & Execution",
        desc: "We develop a detailed migration roadmap with minimal downtime, execute data and application migrations in phases, and validate each stage to ensure zero data loss and business continuity."
      },
      {
        title: "Security & Compliance",
        desc: "We implement cloud-native security measures including identity and access management, network security groups, encryption at rest and in transit, and compliance with industry standards."
      },
      {
        title: "Monitoring & Cost Optimization",
        desc: "We set up comprehensive monitoring dashboards, automated alerting, and implement cost optimization strategies such as reserved instances, spot instances, and right-sizing recommendations."
      }
    ],
    whyChooseUs: [
      {
        title: "Multi-Cloud Expertise",
        desc: "We are certified across AWS, Microsoft Azure, and Google Cloud Platform, enabling us to recommend and implement the cloud provider that best fits your specific workload requirements and budget."
      },
      {
        title: "Zero-Downtime Migrations",
        desc: "Our proven migration methodology ensures business continuity throughout the transition process. We use blue-green deployments and staged cutover strategies to minimize risk and disruption."
      },
      {
        title: "Cost Transparency",
        desc: "We provide detailed cost projections before migration and implement ongoing cost monitoring and optimization to ensure your cloud spending delivers maximum value without unexpected expenses."
      },
      {
        title: "24/7 Infrastructure Support",
        desc: "Our cloud operations team provides round-the-clock monitoring and support with defined SLAs, ensuring your infrastructure remains available, performant, and secure at all times."
      }
    ],
    tools: ["AWS", "Microsoft Azure", "Google Cloud Platform", "Terraform", "Docker", "Kubernetes", "Jenkins", "CloudWatch"],
    faq: [
      {
        q: "What are the benefits of moving to the cloud?",
        a: "Cloud migration offers numerous benefits including reduced infrastructure costs, automatic scalability, improved disaster recovery, enhanced collaboration, automatic software updates, and the ability to work from anywhere with an internet connection."
      },
      {
        q: "Is cloud hosting secure?",
        a: "Major cloud providers invest billions in security infrastructure and comply with international security standards. When properly configured with encryption, access controls, and monitoring, cloud environments can be more secure than traditional on-premise servers."
      },
      {
        q: "How long does cloud migration take?",
        a: "Timeline depends on the size and complexity of your infrastructure. A small business with standard workloads can migrate in 2-4 weeks, while enterprise environments with complex integrations may require 2-6 months for a complete migration."
      }
    ]
  },
  {
    id: 11,
    slug: "domain-hosting",
    title: "Domain & Hosting Services",
    shortDesc: "Reliable domain registration, high-performance web hosting, and professional email solutions to establish your online presence.",
    icon: "ri-server-line",
    color: "green",
    image: "https://images.unsplash.com/photo-1558494949-ef010cbdcc31?w=800&h=400&fit=crop",
    approach: [
      {
        title: "Domain Selection & Registration",
        desc: "We help you choose a domain name that is memorable, brandable, and SEO-friendly. We handle the registration process, DNS configuration, and domain privacy protection."
      },
      {
        title: "Hosting Plan Selection",
        desc: "Based on your website's traffic expectations, resource requirements, and budget, we recommend the optimal hosting plan — shared, VPS, dedicated, or managed cloud hosting."
      },
      {
        title: "Server Setup & Configuration",
        desc: "We configure your hosting environment with optimal server settings, PHP versions, database configurations, caching mechanisms, and security hardening for peak performance."
      },
      {
        title: "SSL & Security Setup",
        desc: "We install and configure SSL certificates, set up firewall rules, implement malware scanning, configure automated backups, and harden server security against common threats."
      },
      {
        title: "Email & Monitoring",
        desc: "We set up professional business email accounts with your domain name, configure spam filtering, and implement uptime monitoring with automated alerts for any service disruptions."
      }
    ],
    whyChooseUs: [
      {
        title: "99.9 Percent Uptime Guarantee",
        desc: "Our hosting infrastructure is built on redundant systems with automatic failover, ensuring your website remains accessible to visitors and customers around the clock with minimal downtime."
      },
      {
        title: "Daily Automated Backups",
        desc: "We configure automated daily backups with easy restoration options, giving you peace of mind that your data is safe and recoverable in case of any unexpected issues."
      },
      {
        title: "Scalable Resources",
        desc: "As your business grows, your hosting can scale seamlessly. We offer easy resource upgrades for CPU, RAM, storage, and bandwidth without any downtime or migration headaches."
      },
      {
        title: "Expert Technical Support",
        desc: "Our support team is available to assist with server issues, DNS configurations, email problems, and any hosting-related technical challenges you may encounter."
      }
    ],
    tools: ["cPanel", "Plesk", "Cloudflare", "AWS Route 53", "Let's Encrypt", "Google Workspace", "Microsoft 365", "LiteSpeed"],
    faq: [
      {
        q: "What type of hosting do I need?",
        a: "For new websites with low traffic, shared hosting is cost-effective. Growing businesses benefit from VPS hosting for better performance and control. High-traffic websites and e-commerce stores should consider dedicated or managed cloud hosting for maximum reliability."
      },
      {
        q: "Can I transfer my existing domain and hosting?",
        a: "Yes. We handle the complete migration process including domain transfer, DNS updates, website files, databases, and email accounts — ensuring zero downtime during the transition."
      },
      {
        q: "Do you provide business email hosting?",
        a: "Yes. We set up professional email accounts using your domain name (yourname@yourcompany.com) through Google Workspace or Microsoft 365, giving your business a more credible and professional image."
      }
    ]
  },
  {
    id: 12,
    slug: "ai-ml-solutions",
    title: "AI & Machine Learning Solutions",
    shortDesc: "Intelligent automation and predictive analytics solutions that transform raw data into actionable business intelligence.",
    icon: "ri-robot-line",
    color: "purple",
    image: "https://images.unsplash.com/photo-1677442136019-21780ecad995?w=800&h=400&fit=crop",
    approach: [
      {
        title: "Use Case Identification",
        desc: "We work with your team to identify high-impact AI opportunities within your business processes — from customer service automation and demand forecasting to quality inspection and personalization engines."
      },
      {
        title: "Data Assessment & Preparation",
        desc: "AI is only as good as its data. We assess your data quality, clean and preprocess datasets, engineer relevant features, and establish data pipelines for continuous model training."
      },
      {
        title: "Model Development & Training",
        desc: "Our data scientists select and train appropriate machine learning models — classification, regression, clustering, NLP, or computer vision — using your data and industry best practices."
      },
      {
        title: "Testing & Validation",
        desc: "Models are rigorously tested against holdout datasets, cross-validated for robustness, and evaluated using relevant metrics to ensure they perform reliably in real-world conditions."
      },
      {
        title: "Deployment & Monitoring",
        desc: "We deploy models to production environments with monitoring dashboards that track prediction accuracy, data drift, and model performance — triggering retraining when performance degrades."
      }
    ],
    whyChooseUs: [
      {
        title: "Practical AI Focus",
        desc: "We focus on AI applications that deliver tangible business value — not technology for technology's sake. Every project starts with a clear business case and measurable success criteria."
      },
      {
        title: "End-to-End Capability",
        desc: "From data engineering and model development to deployment and monitoring, our team handles the complete AI lifecycle without requiring you to manage multiple vendors."
      },
      {
        title: "Explainable AI",
        desc: "We build models with interpretability in mind, providing clear explanations for predictions and decisions — essential for regulated industries and building stakeholder trust."
      },
      {
        title: "Ethical AI Practices",
        desc: "We implement fairness checks, bias detection, and responsible AI principles throughout the development process to ensure your AI systems are ethical and trustworthy."
      }
    ],
    tools: ["Python", "TensorFlow", "PyTorch", "Scikit-learn", "OpenAI API", "Hugging Face", "AWS SageMaker", "Google Vertex AI"],
    faq: [
      {
        q: "Does my business really need AI?",
        a: "AI adds the most value when you have large volumes of data, repetitive decision-making processes, or personalization needs. We offer a free initial consultation to assess whether AI is the right fit for your specific business challenges."
      },
      {
        q: "How much data do I need for AI?",
        a: "It varies by use case. Simple classification models may need hundreds of examples, while complex deep learning models may need thousands or millions. Transfer learning and pre-trained models can reduce data requirements significantly."
      },
      {
        q: "Can you build chatbots for our website?",
        a: "Yes. We build intelligent chatbots powered by natural language processing that can handle customer inquiries, qualify leads, schedule appointments, and provide 24/7 support — integrating with your website, WhatsApp, or other messaging platforms."
      }
    ]
  },
  {
    id: 13,
    slug: "cybersecurity",
    title: "Cybersecurity Services",
    shortDesc: "Comprehensive security assessments, protection strategies, and incident response plans to safeguard your digital assets.",
    icon: "ri-shield-check-line",
    color: "red",
    image: "https://images.unsplash.com/photo-1550751827-4bd374c3f58b?w=800&h=400&fit=crop",
    approach: [
      {
        title: "Security Assessment",
        desc: "We conduct thorough vulnerability assessments and penetration testing across your network, applications, cloud infrastructure, and endpoints to identify security weaknesses before attackers do."
      },
      {
        title: "Risk Analysis & Prioritization",
        desc: "Identified vulnerabilities are analyzed for potential business impact, likelihood of exploitation, and remediation complexity — enabling you to prioritize security investments where they matter most."
      },
      {
        title: "Security Architecture Design",
        desc: "We design defense-in-depth security architectures incorporating firewalls, intrusion detection systems, endpoint protection, network segmentation, and zero-trust access models."
      },
      {
        title: "Implementation & Hardening",
        desc: "Our team implements security controls, configures access policies, deploys monitoring tools, and hardens systems according to industry benchmarks like CIS Controls and NIST frameworks."
      },
      {
        title: "Training & Incident Response",
        desc: "We train your employees on security awareness, phishing recognition, and safe practices. We also develop incident response plans to minimize damage and recovery time in case of a breach."
      }
    ],
    whyChooseUs: [
      {
        title: "Proactive Approach",
        desc: "We believe prevention is better than cure. Our proactive security monitoring and regular assessments identify and address vulnerabilities before they can be exploited by malicious actors."
      },
      {
        title: "Compliance Expertise",
        desc: "We help you meet regulatory compliance requirements including ISO 27001, SOC 2, GDPR, HIPAA, and PCI DSS — providing documentation, controls implementation, and audit preparation support."
      },
      {
        title: "24/7 Security Monitoring",
        desc: "Our security operations center provides continuous monitoring of your infrastructure with real-time threat detection, automated alerting, and rapid incident response capabilities."
      },
      {
        title: "Employee Training Programs",
        desc: "Human error remains the leading cause of security breaches. Our comprehensive security awareness training programs reduce phishing susceptibility and build a security-conscious organizational culture."
      }
    ],
    tools: ["Nessus", "Burp Suite", "Wireshark", "Metasploit", "CrowdStrike", "Splunk", "OWASP ZAP", "Cloudflare WAF"],
    faq: [
      {
        q: "How often should we conduct security assessments?",
        a: "We recommend comprehensive security assessments at least annually, with vulnerability scans performed quarterly. Additionally, assessments should be conducted after major system changes, new application deployments, or security incidents."
      },
      {
        q: "What is the difference between vulnerability assessment and penetration testing?",
        a: "Vulnerability assessment identifies and catalogs potential security weaknesses in your systems. Penetration testing goes further by actively attempting to exploit those vulnerabilities to determine real-world risk and potential impact."
      },
      {
        q: "What should we do if we suspect a security breach?",
        a: "Immediately isolate affected systems, change credentials, and contact our incident response team. We provide rapid response services to contain the breach, assess the damage, preserve evidence, and initiate recovery procedures."
      }
    ]
  },
  {
    id: 14,
    slug: "api-development",
    title: "API Development & Integration",
    shortDesc: "Robust, well-documented APIs that connect your systems, automate workflows, and unlock new possibilities for your business.",
    icon: "ri-link",
    color: "teal",
    image: "https://images.unsplash.com/photo-1558494949-ef010cbdcc31?w=800&h=400&fit=crop",
    approach: [
      {
        title: "Requirements & Architecture",
        desc: "We analyze your integration needs, define API contracts, select appropriate protocols (REST, GraphQL, WebSocket), and design scalable architecture patterns including API gateways and microservices."
      },
      {
        title: "API Design & Documentation",
        desc: "We follow API-first design principles, creating OpenAPI specifications and interactive documentation that makes it easy for developers to understand, test, and integrate with your APIs."
      },
      {
        title: "Development & Security",
        desc: "We build APIs with proper authentication (OAuth 2.0, JWT), rate limiting, input validation, error handling, and CORS policies — ensuring they are secure, reliable, and performant."
      },
      {
        title: "Testing & Quality Assurance",
        desc: "Every endpoint undergoes unit testing, integration testing, load testing, and security testing. We use automated test suites to catch regressions and ensure consistent behavior across updates."
      },
      {
        title: "Deployment & Versioning",
        desc: "We deploy APIs with proper versioning strategies, backward compatibility, deprecation policies, and monitoring dashboards that track usage, latency, and error rates in production."
      }
    ],
    whyChooseUs: [
      {
        title: "Integration Specialists",
        desc: "We have integrated systems across diverse industries — connecting CRMs with marketing platforms, payment gateways with e-commerce stores, IoT devices with analytics dashboards, and much more."
      },
      {
        title: "Scalable Design",
        desc: "Our APIs are designed to handle growth — from hundreds to millions of requests per day — through horizontal scaling, caching strategies, database optimization, and efficient code architecture."
      },
      {
        title: "Comprehensive Documentation",
        desc: "We deliver interactive API documentation with code samples, request/response examples, and sandbox environments that make integration straightforward for any development team."
      },
      {
        title: "Third-Party Expertise",
        desc: "We have extensive experience integrating with popular platforms including Salesforce, HubSpot, Stripe, Razorpay, Twilio, SendGrid, Google APIs, and hundreds of other services."
      }
    ],
    tools: ["Node.js", "Express", "Postman", "Swagger", "GraphQL", "Redis", "RabbitMQ", "Kong API Gateway"],
    faq: [
      {
        q: "What is an API and why do I need one?",
        a: "An API (Application Programming Interface) is a set of rules that allows different software systems to communicate with each other. APIs enable you to connect your website with payment processors, share data between your CRM and marketing tools, build mobile apps that access your backend, and automate repetitive tasks."
      },
      {
        q: "What is the difference between REST and GraphQL?",
        a: "REST uses fixed endpoints for each resource and returns predefined data structures. GraphQL uses a single endpoint where clients specify exactly what data they need. REST is simpler and well-established; GraphQL offers more flexibility and efficiency for complex data requirements."
      },
      {
        q: "Can you integrate our existing software with third-party tools?",
        a: "Yes. We specialize in connecting existing systems through APIs — whether it is syncing your inventory management with your e-commerce platform, connecting your CRM with email marketing tools, or automating data flow between accounting and operations software."
      }
    ]
  },
  {
    id: 15,
    slug: "it-consulting",
    title: "IT Consulting",
    shortDesc: "Expert technology guidance that aligns your IT strategy with business objectives, optimizes operations, and drives digital transformation.",
    icon: "ri-lightbulb-line",
    color: "orange",
    image: "https://images.unsplash.com/photo-1552664730-d307ca884978?w=800&h=400&fit=crop",
    approach: [
      {
        title: "Technology Assessment",
        desc: "We conduct a comprehensive evaluation of your current technology ecosystem — hardware, software, processes, and skills — to identify gaps, redundancies, and opportunities for improvement."
      },
      {
        title: "Strategic Roadmap Development",
        desc: "Based on your business goals and technology assessment, we develop a prioritized technology roadmap with clear milestones, budgets, and expected outcomes for each initiative."
      },
      {
        title: "Vendor & Solution Selection",
        desc: "We evaluate and recommend technology solutions and vendors based on your specific requirements, budget constraints, integration needs, and long-term scalability considerations."
      },
      {
        title: "Implementation Oversight",
        desc: "We provide project management and technical oversight during implementation to ensure projects stay on schedule, within budget, and aligned with the original strategic objectives."
      },
      {
        title: "Continuous Optimization",
        desc: "Technology needs evolve continuously. We provide ongoing advisory services, periodic reviews, and strategic adjustments to ensure your technology investments continue to deliver maximum value."
      }
    ],
    whyChooseUs: [
      {
        title: "Business-First Approach",
        desc: "We start with your business challenges and goals — not technology preferences. Our recommendations are driven by what will deliver the most business value, not by vendor partnerships or technology trends."
      },
      {
        title: "Cross-Industry Experience",
        desc: "Our consultants have advised organizations across manufacturing, healthcare, retail, finance, education, and logistics — bringing diverse perspectives and proven strategies to every engagement."
      },
      {
        title: "Vendor Neutral",
        desc: "As an independent consulting firm, we have no obligations to specific technology vendors. Our recommendations are based purely on what is best for your business needs and objectives."
      },
      {
        title: "Measurable Outcomes",
        desc: "We define clear KPIs and success metrics for every engagement, ensuring that technology investments deliver quantifiable improvements in efficiency, revenue, cost savings, or customer satisfaction."
      }
    ],
    tools: ["Jira", "Confluence", "Miro", "Lucidchart", "Microsoft Visio", "Slack", "Notion", "Trello"],
    faq: [
      {
        q: "When should I hire an IT consultant?",
        a: "IT consultants add the most value during major technology decisions — system migrations, digital transformation initiatives, security incidents, rapid growth phases, or when you lack specific technical expertise in-house."
      },
      {
        q: "How is IT consulting different from managed IT services?",
        a: "IT consulting provides strategic advice, planning, and project-based guidance. Managed IT services provide ongoing, day-to-day management of your technology infrastructure. We offer both, depending on your needs."
      },
      {
        q: "Do you provide consulting for startups?",
        a: "Absolutely. We help startups make smart technology choices from the beginning — selecting scalable architectures, choosing cost-effective tools, establishing development processes, and building a technology foundation that supports rapid growth."
      }
    ]
  },
  {
    id: 16,
    slug: "video-animation",
    title: "Video & Animation",
    shortDesc: "Compelling video content and motion graphics that tell your brand story, explain complex concepts, and captivate your audience.",
    icon: "ri-video-line",
    color: "green",
    image: "https://images.unsplash.com/photo-1574717024653-61fd2cf4d44d?w=800&h=400&fit=crop",
    approach: [
      {
        title: "Creative Brief & Scripting",
        desc: "We develop a clear creative brief defining the video's objective, target audience, key messages, and tone. Our scriptwriters craft compelling narratives that communicate your message within the optimal duration."
      },
      {
        title: "Storyboarding",
        desc: "Detailed storyboards visualize each scene, transition, and visual element — providing a clear blueprint for production and ensuring alignment before any filming or animation begins."
      },
      {
        title: "Production & Animation",
        desc: "For live-action videos, we handle filming, lighting, and audio recording. For animations, our artists create custom illustrations, characters, and motion graphics that bring your story to life."
      },
      {
        title: "Post-Production",
        desc: "Professional editing, color grading, sound design, voiceover integration, background music, and special effects transform raw footage and assets into polished, broadcast-quality content."
      },
      {
        title: "Optimization & Distribution",
        desc: "We optimize videos for different platforms — YouTube, Instagram, LinkedIn, websites — with appropriate dimensions, durations, captions, and thumbnails to maximize reach and engagement."
      }
    ],
    whyChooseUs: [
      {
        title: "Full-Service Production",
        desc: "From concept to distribution, we handle every aspect of video production under one roof — eliminating the complexity of coordinating multiple freelancers or agencies."
      },
      {
        title: "Multi-Format Expertise",
        desc: "We create diverse video formats including explainer videos, product demos, testimonials, corporate presentations, social media reels, animated infographics, and training materials."
      },
      {
        title: "Brand Consistent",
        desc: "Every video we produce maintains strict consistency with your brand identity — colors, fonts, tone of voice, and visual style — reinforcing brand recognition across all touchpoints."
      },
      {
        title: "Engagement Optimized",
        desc: "We structure videos for maximum viewer retention — hooking attention in the first 3 seconds, delivering value throughout, and ending with clear calls to action that drive desired outcomes."
      }
    ],
    tools: ["Adobe Premiere Pro", "After Effects", "DaVinci Resolve", "Blender", "Cinema 4D", "Audition", "Final Cut Pro", "Lottie"],
    faq: [
      {
        q: "What types of videos do you produce?",
        a: "We produce a wide range including corporate videos, explainer animations, product demonstrations, customer testimonials, social media reels, training videos, event coverage, and animated infographics."
      },
      {
        q: "How long should a marketing video be?",
        a: "Ideal length depends on the platform and purpose. Social media ads perform best at 15-30 seconds, explainer videos at 60-90 seconds, and corporate or training videos can be 3-5 minutes. We recommend the optimal duration based on your specific goals."
      },
      {
        q: "Do you provide voiceover and music?",
        a: "Yes. We offer professional voiceover recording in multiple languages, licensed background music selection, and custom sound design. We ensure all audio elements complement the visual narrative and enhance viewer engagement."
      }
    ]
  }
];

export default servicesData;